import * as React from 'react';
import { PartialThemeIds, ThemeIds } from '@porter/shared-theme';
import { AccordionItem } from '../../interfaces';
import { BUSINESS_WEB_ORIGINS } from '../../constants/global';
import queryClient from '../../utils/react-query/client';

/**
 * Query key prefixes that carry the header image. Partial keys match by prefix,
 * so these cover every variant without touching unrelated caches — payments,
 * chats and gift cards have no business being refetched here.
 */
const IMAGE_QUERY_KEYS = [['employee'], ['business-path'], ['business-by-id']];

declare global {
  interface Window {
    __PORTER_PREVIEW__?: boolean;
    /** Present only inside a React Native WebView. */
    ReactNativeWebView?: { postMessage: (message: string) => void };
  }
}

/**
 * True when the booking site customizer is iframing this page. Read from the
 * global the document head sets, so analytics and React agree on one answer.
 */
// eslint-disable-next-line no-underscore-dangle
export const IS_PREVIEW = !!window.__PORTER_PREVIEW__;

function isAllowedOrigin(origin: string) {
  if (BUSINESS_WEB_ORIGINS.includes(origin)) return true;
  // The mobile customizer drives this page in a WebView, where there is no
  // parent frame — so its pushes are same-origin and carry this page's origin
  // rather than a customizer's.
  return !!window.ReactNativeWebView && origin === window.location.origin;
}

function sameIds(a: PartialThemeIds | null, b: PartialThemeIds | null) {
  if (!a || !b) return a === b;
  return (
    a.backgroundId === b.backgroundId &&
    a.primaryId === b.primaryId &&
    a.accentId === b.accentId &&
    a.fontId === b.fontId
  );
}

function sameSocials(a: Partial<PreviewSocials>, b: Partial<PreviewSocials>) {
  return (
    a.instagramHandle === b.instagramHandle &&
    a.tiktokHandle === b.tiktokHandle &&
    a.facebookUrl === b.facebookUrl &&
    a.websiteUrl === b.websiteUrl
  );
}

function sameGallery(a: string[] | null, b: string[] | null) {
  if (!a || !b) return a === b;
  return a.length === b.length && a.every((ref, index) => ref === b[index]);
}

function sameFaqs(a: AccordionItem[] | null, b: AccordionItem[] | null) {
  if (!a || !b) return a === b;
  return (
    a.length === b.length &&
    a.every(
      (faq, index) =>
        faq.faqId === b[index].faqId &&
        faq.header === b[index].header &&
        faq.text === b[index].text,
    )
  );
}

export interface PreviewSocials {
  instagramHandle: string | null;
  tiktokHandle: string | null;
  facebookUrl: string | null;
  websiteUrl: string | null;
}

export interface PreviewOverrides {
  /** Null once the customizer discards, or while nothing is selected. */
  ids: PartialThemeIds | null;
  /**
   * Only the fields the push actually carried. A customizer that edits the
   * theme alone sends none, and an absent field has to keep the saved value
   * rather than blanking the row it is previewing.
   */
  socials: Partial<PreviewSocials>;
  gallery: string[] | null;
  faqs: AccordionItem[] | null;
}

export function pushedSocials(value: unknown): Partial<PreviewSocials> {
  if (!value || typeof value !== 'object') return {};
  const pushed = value as Partial<PreviewSocials>;
  const socials: Partial<PreviewSocials> = {};
  if ('instagramHandle' in pushed) {
    socials.instagramHandle = pushed.instagramHandle ?? null;
  }
  if ('tiktokHandle' in pushed) {
    socials.tiktokHandle = pushed.tiktokHandle ?? null;
  }
  if ('facebookUrl' in pushed) socials.facebookUrl = pushed.facebookUrl ?? null;
  if ('websiteUrl' in pushed) socials.websiteUrl = pushed.websiteUrl ?? null;
  return socials;
}

/**
 * The selection carried on the URL, if any.
 *
 * A native host mounts this page fresh every time its card comes back, and a
 * pushed payload cannot arrive before the first paint — so the customizer puts
 * the selection in the query string and the page opens already wearing it.
 * Pushes then only have to carry what changes while it stays open.
 */
function overridesFromUrl(): PreviewOverrides | null {
  const params = new URLSearchParams(window.location.search);
  const backgroundId = params.get('backgroundId');
  if (!backgroundId) return null;

  return {
    ids: {
      backgroundId,
      primaryId: params.get('primaryId'),
      accentId: params.get('accentId'),
      fontId: params.get('fontId'),
    },
    // The query string carries the theme only; socials and photos stay as
    // saved until a push says otherwise.
    socials: {},
    gallery: null,
    faqs: null,
  };
}

/**
 * Unpublished edits pushed by the customizer, or read off the URL when it
 * opened the page. Only messages from a known business-web origin are accepted.
 */
export function usePreviewOverrides() {
  const [overrides, setOverrides] = React.useState<PreviewOverrides | null>(
    () => (IS_PREVIEW ? overridesFromUrl() : null),
  );

  React.useEffect(() => {
    if (!IS_PREVIEW) return undefined;

    const onMessage = (event: MessageEvent) => {
      if (!isAllowedOrigin(event.origin)) return;
      if (
        event.data?.type !== 'porter:theme' &&
        event.data?.type !== 'porter:refresh'
      ) {
        return;
      }

      if (event.data.type === 'porter:refresh') {
        // The customizer saved something outside the theme ids — the logo, for
        // instance. Refetch rather than accepting a pushed value, so the page
        // keeps its own precedence rules and there is one source of truth.
        IMAGE_QUERY_KEYS.forEach(queryKey =>
          queryClient.invalidateQueries({ queryKey }),
        );
        return;
      }

      const theme = event.data.theme as ThemeIds | null | undefined;
      // A null theme is the customizer discarding, or nothing selected yet —
      // either way the page goes back to unthemed. Socials are independent of
      // that and always come through.
      const ids =
        theme && theme.backgroundId
          ? {
              backgroundId: theme.backgroundId,
              primaryId: theme.primaryId,
              accentId: theme.accentId,
              fontId: theme.fontId,
            }
          : null;
      const socials = pushedSocials(event.data.socials);
      const hasGalleryKey = 'gallery' in event.data;
      const parsedGallery = Array.isArray(event.data.gallery)
        ? (event.data.gallery as string[])
        : null;
      const hasFaqsKey = 'faqs' in event.data;
      const parsedFaqs = Array.isArray(event.data.faqs)
        ? (event.data.faqs as AccordionItem[])
        : null;

      // Same payload in means same state out. The customizer pushes again on
      // load and on the ready handshake, and a fresh object here would
      // re-render the page for something that changed nothing.
      setOverrides(prev => {
        const gallery = hasGalleryKey ? parsedGallery : prev?.gallery ?? null;
        const faqs = hasFaqsKey ? parsedFaqs : prev?.faqs ?? null;
        return prev &&
          sameIds(prev.ids, ids) &&
          sameSocials(prev.socials, socials) &&
          sameGallery(prev.gallery, gallery) &&
          sameFaqs(prev.faqs, faqs)
          ? prev
          : { ids, socials, gallery, faqs };
      });
    };

    window.addEventListener('message', onMessage);

    // Tell the customizer we are listening so it pushes the first payload. The
    // parent origin is unknown here, so announce to each allowed one.
    const ready = { type: 'porter:preview-ready' };
    BUSINESS_WEB_ORIGINS.forEach(
      origin => window.parent?.postMessage(ready, origin),
    );
    // The native host has no origin to announce to.
    window.ReactNativeWebView?.postMessage(JSON.stringify(ready));

    return () => window.removeEventListener('message', onMessage);
  }, []);

  return overrides;
}

export function useGalleryPreviewOverride(): string[] | null {
  const overrides = usePreviewOverrides();
  return overrides?.gallery ?? null;
}

export function useFaqsPreviewOverride(): AccordionItem[] | null {
  const overrides = usePreviewOverrides();
  return overrides?.faqs ?? null;
}

export function useGalleryHeroImages<T>(
  publishedImages: T[] | undefined,
): T[] | { imageRef: string; coverPhoto: boolean }[] | undefined {
  const galleryOverride = useGalleryPreviewOverride();
  if (!galleryOverride) return publishedImages;
  return galleryOverride.map((imageRef, index) => ({
    imageRef,
    coverPhoto: index === 0,
  }));
}
